<template>
  <div class="main">
    <!-- Add Task Button -->
    <b-row class="align-items-center">
      <b-col md="8">
        <!-- <h3>{{ projectName }}</h3> -->
      </b-col>
      <b-col md="4">
        <div class="d-flex justify-content-end align-items-center">
          <b-form-checkbox v-model="displayKanban" name="check-button" switch inline class="ml-1">
            Kanban
          </b-form-checkbox>
          <b-modal id="modal-add-task" ok-only ok-title="Add" centered size="lg" title="Add Task" :hide-footer="true"
            ref="addProjectModal">
            <AddTask :task="defaultTask" @taskAdded="closeAddProjectModal" />
          </b-modal>
          <b-button variant="primary" v-b-modal.modal-add-task>
            <feather-icon icon="PlusIcon" class="mr-50" />
            Add Task
          </b-button>
        </div>
      </b-col>
    </b-row>

    <b-modal id="modal-edit-task" ok-only ok-title="Edit" centered size="lg" title="Edit Task" :hide-footer="true"
      ref="editProjectModal">
      <EditTask :task="currentTask" @taskUpdated="closeEditProjectModal" />
    </b-modal>

    <b-modal id="modal-view-task" ok-only ok-title="View" centered size="lg" title="View Task" :hide-footer="true"
      ref="editProjectModal">
      <EditTask :task="currentTask" @taskUpdated="closeEditProjectModal" :isView="true" />
    </b-modal>

    <!-- When Projects not available show this message -->
    <div v-if="tasks.length === 0 && !loading">
      <b-row class="justify-content-center">
        <b-col md="4">
          <b-card>
            <b-card-body class="text-center">
              <h2>No tasks available</h2>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <!-- When Task available show table or kanban -->
    <div class="d-block" v-else>
      <!-- Switch, toggle between task list and kanban -->
      <b-row class="justify-content-end my-1">
        <b-col md="6" class="d-flex justify-content-end align-items-center">
        </b-col>
      </b-row>

      <!-- Table row -->
      <b-row class="justify-content-between table-row" v-if="!displayKanban">
        <b-col md="2" sm="4" class="my-1">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">Per page</label>
            <b-form-select id="perPageSelect" v-model="perPage" size="sm" :options="pageOptions" class="w-50" />
          </b-form-group>
        </b-col>

        <b-col cols="12" class="d-flex justify-content-end mb-2" style="position: relative; z-index: 1">
          <b-button variant="outline-danger" size="sm" @click="clearFilters">
            Clear Filters
          </b-button>
        </b-col>

        <b-col cols="12" v-if="!loading">
          <div v-show="selectedOption === 'task'" class="table-task">
            <b-table striped hover responsive :items="filteredTasks" :fields="fields" :per-page="perPage"
              :current-page="currentPage" :filter="filter" @filtered="onFiltered">
              <!-- Title Filter -->
              <template #head(title)="scope">
                <div class="d-flex flex-column">
                  <span>Title</span>
                  <b-form-input v-model="filters.title" placeholder="Filter by Title" size="sm" @input="applyFilters" />
                </div>
              </template>

              <!-- Assignee(s) Filter -->
              <template #head(assigneeName)="scope">
                <div class="d-flex flex-column">
                  <span>Assignee(s)</span>
                  <b-form-input v-model="filters.assigneeName" placeholder="Filter by Assignee" size="sm"
                    @input="applyFilters" />
                </div>
              </template>

              <!-- Priority Filter -->
              <template #head(priority)="scope">
                <div class="d-flex flex-column">
                  <span>Priority</span>
                  <b-form-select v-model="filters.priority" :options="priorityOptions" placeholder="Select Priority"
                    size="sm" @change="applyFilters" />
                </div>
              </template>

              <!-- Status Filter -->
              <template #head(status)="scope">
                <div class="d-flex flex-column">
                  <span>Status</span>
                  <b-form-select v-model="filters.status" :options="statusOptions" placeholder="Select Status" size="sm"
                    @change="applyFilters" />
                </div>
              </template>
              <!-- Start Date Filter -->
              <template #head(startDate)="scope">
                <div class="d-flex flex-column">
                  <span>Start Date</span>
                  <DatePicker v-model="filters.startDateRange" range format="YYYY-MM-DD" placeholder="Select date range"
                    @change="applyFilters" size="sm" style="max-width: 180px" />
                </div>
              </template>

              <!-- End Date Filter -->
              <template #head(endDate)="scope">
                <div class="d-flex flex-column">
                  <span>End Date</span>
                  <DatePicker v-model="filters.endDateRange" range format="YYYY-MM-DD" placeholder="Select date range"
                    @change="applyFilters" size="sm" style="max-width: 180px"/>
                </div>
              </template>
              <template #head(milestoneName)="scope">
                <div class="d-flex flex-column">
                  <span>Milestone</span>
                  <b-form-input v-model="filters.milestoneName" placeholder="Filter by Milestone" size="sm"
                    @input="applyFilters" />
                </div>
              </template>
              <!-- Total Sub Tasks Filter -->
              <!-- <template #head(totalSubTasks)="scope">
                <div class="d-flex flex-column">
                  <span>Total Sub Tasks</span>
                  <b-form-input
                    v-model="filters.totalSubTasks"
                    placeholder="Filter by Total Sub Tasks"
                    type="number"
                    size="sm"
                    @input="applyFilters"
                  />
                </div>
              </template> -->
              <template #cell(status)="data">
                <b-badge :variant="statusMap[data.item.status].variant">
                  {{ statusMap[data.item.status].text }}
                </b-badge>
              </template>
              <template #cell(milestoneName)="data">
                <span>{{ data.item.milestonesName || 'N/A' }}</span>
              </template>

              <template #cell(startDate)="data">
                <b-badge v-if="
                  formatStartDate(data.item).isOverdue &&
                  formatStartDate(data.item).isCompleted === false
                " variant="danger">
                  {{ formatStartDate(data.item).formattedDate }}
                </b-badge>
                <b-badge v-else-if="
                  formatStartDate(data.item).isOverdue &&
                  formatStartDate(data.item).isCompleted === true
                " variant="success">
                  {{ formatStartDate(data.item).formattedDate }}
                </b-badge>
                <span v-else>
                  {{ formatStartDate(data.item).formattedDate }}
                </span>
              </template>
              <template #cell(endDate)="data">
                <b-badge v-if="
                  formatEndDate(data.item).isOverdue &&
                  formatEndDate(data.item).isCompleted === false
                " variant="danger">
                  {{ formatEndDate(data.item).formattedDate }}
                </b-badge>
                <b-badge v-else-if="
                  formatEndDate(data.item).isOverdue &&
                  formatEndDate(data.item).isCompleted === true
                " variant="success">
                  {{ formatEndDate(data.item).formattedDate }}
                </b-badge>
                <span v-else>
                  {{ formatEndDate(data.item).formattedDate }}
                </span>
              </template>
              <!-- Priority Column -->
              <template #cell(priority)="data">
                <b-badge :variant="priorityMap[data.item.priority].variant">
                  {{ priorityMap[data.item.priority].text }}
                </b-badge>
              </template>
              <template v-slot:cell(actions)="data">
                <b-dropdown variant="link" toggle-class="p-0" no-caret class="m-1">
                  <template #button-content>
                    <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body" />
                  </template>
                  <b-dropdown-item v-if="data.item.status !== 'COMPLETED'" v-b-modal.modal-edit-task
                    @click="EditTask(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50">Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="data.item.status === 'COMPLETED'" v-b-modal.modal-view-task
                    @click="EditTask(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50">View</span>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="data.item.status === 'NOTSTARTED'" @click="deleteTask(data.item.id)">
                    <feather-icon icon="TrashIcon" />
                    <span class="align-middle ml-50">Delete</span>
                  </b-dropdown-item>
                  <b-dropdown-item :to="{
                    path: `/projectmanagement/subtasks-list/${id}/${data.item.id}`,
                  }">
                    <feather-icon icon="EyeIcon" />
                    <span class="align-middle ml-50">Sub Tasks</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="viewTaskDocument(data.item.id)">
                    <feather-icon icon="PaperclipIcon" />
                    <span class="align-middle ml-50">Documents</span>
                  </b-dropdown-item>

                  <b-dropdown-item v-b-modal.modal-edit :to="addtimesheet(data.item.id, data.item.projectId)">
                    <feather-icon icon="ClockIcon" class="mr-50" />
                    <span>Time sheet</span>
                  </b-dropdown-item>
                </b-dropdown>
              </template>
            </b-table>
          </div>

          <b-modal ref="modal-view-document" id="modal-view-document" ok-only ok-title="View" centered size="lg"
            title="View Documents" :hide-footer="true">
            <DocumentUpload @deleteFile="deleteFile" @downloadFile="downloadFile" @uploadFiles="uploadFiles"
              :documentDetails="documentDetails" />
          </b-modal>
        </b-col>

        <b-col cols="12">
          <div class="d-flex justify-content-center mb-1">
            <b-spinner variant="success" label="Spinning" block v-if="loading"
              style="width: 3rem; height: 3rem"></b-spinner>
          </div>
          <b-pagination v-if="!loading" v-model="currentPage" :total-rows="totalRows" :per-page="perPage" align="center"
            size="sm" class="my-0" />
        </b-col>
      </b-row>

      <!-- Kanban -->
      <b-row class="kanban-row" v-else>
        <b-col md="12">
          <TaskKanban :tasks="tasks" />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import {
  BTable,
  BAvatar,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BCard,
  BCardBody,
  BDropdown,
  BLink,
  BDropdownItem,
  BSpinner,
  BFormCheckbox,
  BModal,
  VBModal,
} from "bootstrap-vue";

import DocumentUpload from "../component/DocumentUpload.vue";
import AddTask from "./AddTask.vue";
import EditTask from "./EditTask.vue";
import TaskKanban from "./Kanban.vue";
import { mapActions, mapGetters } from "vuex";
import { customSwal, customError, customLoader } from "@/libs/customFn";

export default {
  components: {
    BCard,
    BCardBody,
    BTable,
    BAvatar,
    BBadge,
    BRow,
    BCol,
    BModal,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BDropdown,
    BLink,
    BDropdownItem,
    AddTask,
    EditTask,
    BSpinner,
    BFormCheckbox,
    TaskKanban,
    DocumentUpload,
    VBModal,
    DatePicker,
  },
  data() {
    return {
      selectedOption: "task",
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 1,
      currentTask: {},
      currentPage: 1,
      projectName: null,
      filter: null,
      id: this.$route.params.id,
      milestonesId:
        this.$route.params.milestoneId == undefined
          ? null
          : this.$route.params.milestoneId,
      filterOn: [],
      displayKanban: false,
      infoModal: {
        id: "info-modal",
        title: "",
        content: "",
      },
      filters: {
        title: "",
        assigneeName: "",
        priority: null,
        status: null,
        startDateRangeDateRange: [null, null],
        endDateRange: [null, null],
        milestoneName: "",
      },
      priorityOptions: [
        { value: null, text: "All" },
        { value: "LOW", text: "Low" },
        { value: "MEDIUM", text: "Medium" },
        { value: "HIGH", text: "High" },
        { value: "NOTSPECIFIED", text: "Not specified" },
      ],
      statusOptions: [
        { value: null, text: "All" },
        { value: "NOTSTARTED", text: "Not Started" },
        { value: "INPROGRESS", text: "In Progress" },
        { value: "COMPLETED", text: "Completed" },
        // { value: "NEW", text: "New" },
        { value: "ONHOLD", text: "On Hold" },
        // { value: "BLOCKED", text: "Blocked" },
        // { value: "REMOVED", text: "Removed" },
      ],
      filteredTasks: [],
      fields: [
        { key: "title", label: "Title", sortable: true },
        { key: "assigneeName", label: "Assignee(s)", sortable: true },
        { key: "priority", label: "Priority", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "startDate", label: "Start Date", sortable: true },
        { key: "endDate", label: "End Date", sortable: true },
        { key: "milestoneName", label: "Milestone Name", sortable: true },
        // { key: "totalSubTasks", label: "Total Sub Tasks", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      fields_overdue: [
        { key: "title", label: "Title", sortable: true },
        { key: "assigneeName", label: "Assignee(s)", sortable: true },
        { key: "priority", label: "Priority", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "progress", label: "Progress", sortable: true },
        { key: "actions", label: "Actions" },
      ],
      statusMap: {
        NOTSTARTED: { text: "Not Started", variant: "secondary" },
        NEW: { text: "New", variant: "primary" },
        INPROGRESS: { text: "In Progress", variant: "info" },
        ONHOLD: { text: "On Hold", variant: "warning" },
        COMPLETED: { text: "Completed", variant: "success" },
        BLOCKED: { text: "Blocked", variant: "danger" },
        REMOVED: { text: "Removed", variant: "dark" },
      },

      priorityMap: {
        NOTSPECIFIED: { text: "Not Specified", variant: "secondary" },
        LOW: { text: "Low", variant: "info" },
        MEDIUM: { text: "Medium", variant: "warning" },
        HIGH: { text: "High", variant: "danger" },
      },

      overdue_items: [
        {
          title: "Task 1",
          assigneeName: "Assignee 1",
          priority: 1,
          status: 1,
          progress: 1,
        },
      ],
      priority: [
        {
          1: "High",
        },
        {
          1: "danger",
        },
      ],
      status: [
        {
          1: "Not started",
        },
        {
          1: "light-secondary",
        },
      ],
      progress: [
        {
          1: "Overdue",
        },
        {
          1: "light-danger",
        },
      ],

      defaultTask: {
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        assignee: "",
        assigneeName: "",
        priority: "NOTSPECIFIED", // Set default priority
        status: "NOTSTARTED", // Set default status
        projectId: this.$route.params.id,
        milestonesId:
          this.$route.params.milestoneId == undefined
            ? null
            : this.$route.params.milestoneId,
      },
      documentType: "Task",
      documentDetails: {
        isDelete: false,
        documentList: [],
      },
      currentTaskId: null,
    };
  },
  computed: {
    sortOptions() {
      return this.fields
        .filter((f) => f.sortable)
        .map((f) => ({ text: f.label, value: f.key }));
    },

    ...mapGetters("projectModule", {
      projects: "projects",
      tasks: "tasks",
      milestones: "milestones",
      loading: "loading",
    }),
    ...mapGetters("documentModule", {
      documents: "documents",
      file: "file",
      docloading: "docLoader",
    }),
  },
  async mounted() {
   // this.totalRows = this.tasks.length;
    console.log("totaltasks", this.tasks.length);
    await this.getProjectTasksAction({
      id: this.id,
      milestoneId: this.milestonesId,
    })
      .then((response) => {
        this.projectName = this.tasks.map((item) => item.projectName)[0];
        this.filteredTasks = this.tasks; // Populate filteredTasks with all tasks initially
        this.totalRows = this.filteredTasks.length; // Set totalRows for pagination
        console.log(this.tasks);
      })
      .catch((response) => {
        console.log(response);
      });

    this.$emit("updateTasksCount", this.tasks.length);
    this.filteredTasks = this.tasks;
  },
  methods: {
    ...mapActions("projectModule", [
      "getProjectTasksAction",
      "addProjectTaskAction",
      "removeProjectTaskAction",
      "updateProjectTaskAction",
    ]),

    ...mapActions("documentModule", [
      "getDocumentListAction",
      "removeDocumentAction",
      "fileSharingAction",
      "uploadDocumentAction",
      "uploadMultipleDocumentAction",
      "downloadDocumentAction",
    ]),
    clearTask() {
      this.defaultTask = {
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        assignee: "",
        assigneeName: "",
        priority: "NOTSPECIFIED", // Set default priority
        status: "NOTSTARTED", // Set default status
        projectId: this.$route.params.id,
        milestonesId:
          this.$route.params.milestoneId == undefined
            ? null
            : this.$route.params.milestoneId,
      };
    },
    applyFilters() {
      this.filteredTasks = this.tasks.filter((task) => {
        const matchesTitle = task.title
          .toLowerCase()
          .includes(this.filters.title.toLowerCase());
        const matchesAssignee = task.assigneeName
          .toLowerCase()
          .includes(this.filters.assigneeName.toLowerCase());
        const matchesPriority =
          !this.filters.priority || task.priority === this.filters.priority;
        const matchesStatus =
          !this.filters.status || task.status === this.filters.status;
        const startDateRange = this.filters.startDateRange ?? [null, null];
        const endDateRange = this.filters.endDateRange ?? [null, null];
        const matchesStartDate =
          !startDateRange[0] ||
          !startDateRange[1] ||
          (new Date(task.startDate) >= new Date(startDateRange[0]) &&
            new Date(task.startDate) <= new Date(startDateRange[1]));

        const matchesEndDate =
          !endDateRange[0] ||
          !endDateRange[1] ||
          (new Date(task.endDate) >= new Date(endDateRange[0]) &&
            new Date(task.endDate) <= new Date(endDateRange[1]));

        const matchesTotalSubTasks =
          !this.filters.totalSubTasks ||
          task.totalSubTasks === Number(this.filters.totalSubTasks);

        const matchesMilestone =
          !this.filters.milestoneName ||
          (task.milestonesName &&
            task.milestonesName.toLowerCase().includes(this.filters.milestoneName.toLowerCase()));

        return (
          matchesTitle &&
          matchesAssignee &&
          matchesPriority &&
          matchesStatus &&
          matchesStartDate &&
          matchesEndDate &&
          matchesTotalSubTasks &&
          matchesMilestone
        );
      });

      this.totalRows = this.filteredTasks.length;
    },
    clearFilters() {
      this.filters = {
        title: "",
        assigneeName: "",
        priority: null,
        status: null,
        startDateRange: [null, null],
        endDateRange: [null, null],
        totalSubTasks: null,
      };
      this.applyFilters();
    },
    formatEndDate(task) {
      if (!task.endDate) {
        return {
          formattedDate: "No Date",
          isOverdue: false,
          isCompleted: false,
        };
      }
      const date = new Date(task.endDate);
      const today = new Date();
      const isOverdue = date < today;
      const isCompleted = task.status === "COMPLETED";
      const formattedDate = date.toLocaleDateString();
      return { formattedDate, isOverdue, isCompleted };
    },

    formatStartDate(task) {
      if (!task.startDate) {
        return {
          formattedDate: "No Date",
          isOverdue: false,
          isCompleted: false,
        };
      }
      const date = new Date(task.startDate);
      const today = new Date();
      const isOverdue = date < today;
      const isCompleted = task.status === "COMPLETED";
      const formattedDate = date.toLocaleDateString();
      return { formattedDate, isOverdue, isCompleted };
    },



    async closeAddProjectModal() {
      this.$refs.addProjectModal.hide();
      this.clearTask();
      await this.getProjectTasksAction({
        id: this.id,
        milestoneId: this.milestonesId,
      })
        .then((response) => {
          console.log(this.tasks);
          this.filteredTasks = this.tasks;
          this.totalRows = this.filteredTasks.length;
        })
        .catch((response) => {
          console.log(response);
        });

      this.totalRows = this.projects.length;

    },

    async closeEditProjectModal() {
      this.$refs.editProjectModal.hide();
      this.clearTask();

      await this.getProjectTasksAction({
        id: this.id,
        milestoneId: this.milestonesId,
      })
        .then((response) => {
          this.totalRows = this.tasks.length;
          this.filteredTasks = this.tasks;

        })
        .catch((response) => {
          console.log(response);
        });

      this.totalRows = this.projects.length;
    },

    EditTask(task) {
      this.currentTask = task;
    },

    async viewTaskDocument(taskId) {
      this.currentTaskId = taskId;
      await this.getDocumentListAction({
        id: this.id,
        type: this.documentType,
        typeId: this.currentTaskId,
      });
      this.documentDetails.documentList = this.documents;
      this.documentDetails.isDelete = true;
      this.$nextTick(() => {
        this.$bvModal.show("modal-view-document");
      });
    },

    deleteTask(smtpId) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.isConfirmed) {
          this.$swal({
            title: "Deleting...",
            text: "Please wait while the Task are being deleted...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              this.$swal.showLoading();
            },
          });

          this.removeProjectTaskAction(smtpId)
            .then((response) => {
              this.$swal.hideLoading();
              this.$swal.fire(
                "Deleted!",
                "Your Task have been deleted.",
                "success"
              );
              this.getProjectTasksAction({
                id: this.id,
                milestoneId: this.milestonesId,
              })
                .then((response) => {
                  this.filteredTasks = this.tasks;
                  this.totalRows = this.tasks.length;
                  this.currentPage = 1;
                })
                .catch((response) => {
                  console.log(response);
                });
            })
            .catch((error) => {
              this.$swal.fire(
                "Failed to Delete!",
                error.result || "There was a problem deleting the Task.",
                "error"
              );
            });
        }
      });
    },

    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`;
      this.infoModal.content = JSON.stringify(item, null, 2);
      this.$root.$emit("bv::show::modal", this.infoModal.id, button);
    },
    resetInfoModal() {
      this.infoModal.title = "";
      this.infoModal.content = "";
    },
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length; // Ensure totalRows is updated
      this.currentPage = 1; // Reset to the first page
    },
    deleteRow(index) {
      // Confirm deletion
      if (confirm("Are you sure you want to delete this row?")) {
        // Remove the item at the specified index from the items array
        this.items.splice(index, 1);
        // Optionally, you can refresh any dependent data or UI here
      }
    },
    selectOption(option) {
      this.selectedOption = option;
    },
    async downloadFile(id, fileName) {
      let loader = customLoader(
        "Please wait while the document is being prepared for download"
      );

      try {
        const response = await this.downloadDocumentAction(id);
        const url = window.URL.createObjectURL(
          new Blob([response.data], {
            type:
              response.headers["content-type"] || "application/octet-stream",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName); // Use the provided fileName parameter directly
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Clean up DOM by removing the link element after clicking
        window.URL.revokeObjectURL(url); // Free up memory by releasing object URL

        loader.close();
      } catch (error) {
        console.error("Download failed:", error);
        loader.close();
        this.error("Failed to download the file. Please try again."); // Updated error message for better clarity
      }
    },
    async uploadFiles(selectedFiles) {
      let loader = customLoader(
        "Please wait while the document(s) is being added"
      );
      const referenceId = this.id;

      const maxSize = 20 * 1024 * 1024;
      if (this.selectedFiles && this.selectedFiles.size > maxSize) {
        alert('File size exceeds 20 MB limit. Please choose a smaller file.');
        this.submitting = false;
        return;
      }

      if (selectedFiles.length > 1) {
        await this.uploadMultipleDocumentAction({
          payload: selectedFiles,
          referenceId: referenceId,
          type: this.documentType,
          typeId: this.currentTaskId,
        })
          .then((response) => {
            customSwal("Your file(s) have been successfully uploaded.");
            loader.close();
          })
          .catch((error) => {
            customError(error);
            loader.close();
          });
      } else if (selectedFiles.length < 2) {
        await this.uploadDocumentAction({
          payload: selectedFiles,
          referenceId: referenceId,
          type: this.documentType,
          typeId: this.currentTaskId,
        })
          .then((response) => {
            customSwal("Your file(s) have been successfully uploaded.");
            loader.close();
          })
          .catch((error) => {
            customError(error);
            loader.close();
          });
      }

      await this.getDocumentListAction({
        id: referenceId,
        type: this.documentType,
        typeId: this.currentTaskId,
      });
      this.documentDetails.documentList = this.documents;

      this.$nextTick(() => {
        this.$bvModal.show("modal-view-document");
      });
    },
    async deleteFile(docId) {
      let loader = customLoader(
        "Please wait while the document is being deleted"
      );
      await this.removeDocumentAction(docId)
        .then((response) => {
          loader.close();
          //this.$refs['modal-view-testdocument'].hide();
          customSwal();
        })
        .catch((error) => {
          loader.close();
          //this.$refs['modal-view-testdocument'].hide();
          customError(error);
        });

      await this.getDocumentListAction({
        id: this.id,
        type: this.documentType,
        typeId: this.currentTaskId,
      });
      this.documentDetails.documentList = this.documents;

      this.$nextTick(() => {
        this.$bvModal.show("modal-view-document");
      });
    },

    addtimesheet(item, project) {
      return `/projectmanagementtimesheet/add/${item}/${project}`;
    },
  },
};
</script>
